import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './BannerCarousel.module.scss';
import useCarousel from './useCarousel';

const BannerCarouselSlide: FC<React.PropsWithChildren> = ({ children }) => {
  const { addSlide, removeSlide } = useCarousel();
  const [visible, setVisible] = useState<boolean>(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      if (!Array.isArray(entries)) {
        return;
      }

      const entry = entries[0];
      const show = entry.contentRect.height > 0;
      setVisible(show);
    },
    [setVisible]
  );

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }
    const slide = contentRef.current;
    if (visible) {
      addSlide(slide);
    } else {
      removeSlide(slide);
    }
    return () => {
      if (!visible) {
        return;
      }
      removeSlide(slide);
    };
  }, [visible, addSlide, removeSlide]);

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }
    const slide = contentRef.current;
    const show = slide.getBoundingClientRect().height > 0;
    setVisible(show);

    let RO: ResizeObserver | null = new ResizeObserver(handleResize);
    RO.observe(slide);
    return () => {
      if (!RO) {
        return;
      }
      RO.disconnect();
      RO = null;
    };
  }, [handleResize]);

  return (
    <div className={styles.BannerCarouselSlide}>
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export default BannerCarouselSlide;
